import { groupBy, isNil } from "lodash";
import { createSelector } from "@reduxjs/toolkit";
import {
  DEFAULT_SAME_DAY_CUT_OFF_TIME,
  DEFAULT_EARLIEST_READY_TIME,
  DEFAULT_LATEST_READTY_TIME,
  DEFAULT_SATURN_AUTO,
  DEFAULT_MIN_DUTY_TIME,
  DEFAULT_AM_TIME_IN_DEPOT,
  DEFAULT_PM_TIME_IN_DEPOT,
} from "../../consts/depot";
import { ALLOCATED, UNALLOCATED } from "../../consts/routeDetails";
import {
  visiblePostcodes,
  zoomSelector,
  zoomLevelSelector,
} from "../map/selectors";

export const mapEditModeSelector = (state) => !!state.depot.mapEditMode;
export const locationPlanningEnabledSelector = (state) =>
  state.depot.locationPlanningEnabled;
export const pickupPlanningEnabledSelector = (state) =>
  state.depot.pickupPlanningEnabled;
export const depotChecksumSelector = (state) => state.depot.checksum;
export const depotSessionChecksumSelector = (state) =>
  state.depot.sessionChecksum;
export const depotLockSelector = (state) => ({
  lockedByYou: state.depot.lockedByYou,
  lockedInDifferentBrowser: state.depot.lockedInDifferentBrowser,
  lockedByUserCode: state.depot.lockedByUserCode,
  lockedByUserName: state.depot.lockedByUserName,
  lockedByText: !!state.depot.lockedByUserCode
    ? `Depot Locked By ${
        state.depot.lockedByYou ? "You" : state.depot.lockedByUserName
      }`
    : null,
  lockedDatetime:
    state.depot.lockedInDifferentBrowser && state.depot.lockedDatetime,
});
export const selectedArtificialPostcodeSelector = (state) =>
  state.depot.selectedArtificialPostcode;
export const partialPostcodesSelector = (state) => state.depot.partialPostcodes;
export const lookupTableSelector = (state) => state.depot.lookupTables;
export const artificialTablesSelector = (state) => state.depot.artificialTables;
export const fullArtificialTablesSelector = (state) =>
  state.depot.artificialTables?.full;
export const visibleArtificialTablesSelector = (state) =>
  state.depot.artificialTables?.visible;
export const visibleArtificialPostcodesSelector = createSelector(
  visibleArtificialTablesSelector,
  zoomLevelSelector,
  (artificialTables, zoomLevel) => {
    if (!artificialTables) return [];
    else return Object.keys(artificialTables[zoomLevel]);
  }
);
export const visiblePostcodeLabelsSelector = createSelector(
  visiblePostcodes,
  visibleArtificialPostcodesSelector,
  zoomSelector,
  zoomLevelSelector,
  lookupTableSelector,
  (visiblePostcodes, visibleArtificialPostcodes, zoom, level, lookupTables) => {
    if (!lookupTables || !lookupTables[level]) return [];
    const reducePostcodes = (acc, postcode) => {
      const { labelCoordinates, labelMinZoom } =
        lookupTables[level][postcode] || {};
      if (labelCoordinates && (!labelMinZoom || labelMinZoom < zoom))
        acc.push({
          postcode,
          coordinates: labelCoordinates,
        });
      return acc;
    };
    const visiblePostcodeLabels = visiblePostcodes.reduce(reducePostcodes, []);
    return visibleArtificialPostcodes.reduce(
      reducePostcodes,
      visiblePostcodeLabels
    );
  }
);
export const artificialPostcodeInEditSelector = (state) =>
  state.depot.artificialPostcodeEdit;
export const artificialPostcodeInEditCopySelector = (state) =>
  state.depot.artificialPostcodeEditCopy;
export const postcodeViewSelector = (state) => state.depot.postcodeView;
export const listArtificialTablesSelector = createSelector(
  postcodeViewSelector,
  fullArtificialTablesSelector,
  (view, artificialTables) => {
    if (!view || !artificialTables)
      return {
        OUTER: [],
        SECTOR: [],
        POSTCODE: [],
      };
    else if (view === ALLOCATED)
      return Object.keys(artificialTables).reduce(
        (acc, level) => {
          artificialTables[level].forEach((artificialPostcode) => {
            if (
              !isNil(artificialPostcode.lng) &&
              !isNil(artificialPostcode.lat)
            )
              acc[level].push(artificialPostcode);
          });
          return acc;
        },
        {
          OUTER: [],
          SECTOR: [],
          POSTCODE: [],
        }
      );
    else if (view === UNALLOCATED)
      return Object.keys(artificialTables).reduce(
        (acc, level) => {
          artificialTables[level].forEach((artificialPostcode) => {
            if (isNil(artificialPostcode.lng) || isNil(artificialPostcode.lat))
              acc[level].push(artificialPostcode);
          });
          return acc;
        },
        {
          OUTER: [],
          SECTOR: [],
          POSTCODE: [],
        }
      );
    else return artificialTables;
  }
);
export const maxPostcodesSelector = (state) => state.depot.maxPostcodes;
export const maxSectorsSelector = (state) => state.depot.maxSectors;
export const depotLoadingSelector = (state) =>
  state.depot.loading.partialPostcodes ||
  state.depot.loading.lookupTables ||
  state.depot.loading.locking;
export const depotInfoSelector = (state) => state.depot.depotInfo || {};
export const depotDriverValuesSelector = createSelector(
  depotInfoSelector,
  ({
    minDutyTime = DEFAULT_MIN_DUTY_TIME,
    maxAmTimeInDepot = DEFAULT_AM_TIME_IN_DEPOT,
    maxPmTimeInDepot = DEFAULT_PM_TIME_IN_DEPOT,
  }) => ({
    minDutyTime,
    maxAmTimeInDepot,
    maxPmTimeInDepot,
  })
);
export const depotDefaultCollectionValuesSelector = createSelector(
  depotInfoSelector,
  ({
    defaultSameDayCutOff = DEFAULT_SAME_DAY_CUT_OFF_TIME,
    defaultEarliestReadyTime = DEFAULT_EARLIEST_READY_TIME,
    defaultLatestReadyTime = DEFAULT_LATEST_READTY_TIME,
    defaultSaturnAuto = DEFAULT_SATURN_AUTO,
  }) => ({
    defaultSameDayCutOff: (defaultSameDayCutOff?.length === 7
      ? defaultSameDayCutOff
      : DEFAULT_SAME_DAY_CUT_OFF_TIME
    ).map((time) => time.substring(0, 5)),
    defaultEarliestReadyTime: (defaultEarliestReadyTime?.length === 7
      ? defaultEarliestReadyTime
      : DEFAULT_EARLIEST_READY_TIME
    ).map((time) => time.substring(0, 5)),
    defaultLatestReadyTime: (defaultLatestReadyTime?.length === 7
      ? defaultLatestReadyTime
      : DEFAULT_LATEST_READTY_TIME
    ).map((time) => time.substring(0, 5)),
    defaultSaturnAuto:
      defaultSaturnAuto?.length === 7 ? defaultSaturnAuto : DEFAULT_SATURN_AUTO,
  })
);

export const groupedPostcodesSelector = createSelector(
  lookupTableSelector,
  (lookupTables) =>
    lookupTables?.POSTCODE &&
    groupBy(Object.keys(lookupTables.POSTCODE), (key) =>
      key
        .substr(0, key.length - 2)
        .split("  ")
        .join(" ")
    )
);

export const allocatedPostcodesSelector = createSelector(
  partialPostcodesSelector,
  groupedPostcodesSelector,
  (partialPostcodes, groupedPostcodes) => {
    const outer = [];
    const postcodes = [];
    if (partialPostcodes) {
      partialPostcodes.forEach((sector) => {
        outer.push(sector.substr(0, sector.length - 2));
        const key = sector.split(" ").join("");
        if (!groupedPostcodes) {
          return;
        }
        if (groupedPostcodes[key]) {
          postcodes.push(...groupedPostcodes[key]);
        } else if (groupedPostcodes[sector]) {
          postcodes.push(...groupedPostcodes[sector]);
        } else if (
          sector[2] === " " &&
          groupedPostcodes[sector.split(" ").join("  ")]
        ) {
          postcodes.push(...groupedPostcodes[sector.split(" ").join("  ")]);
        }
      });
    }
    return {
      outer: [...new Set(outer)],
      sector: partialPostcodes,
      postcodes: postcodes.flat(),
    };
  }
);

export const selectedDepotFeaturesSelector = createSelector(
  partialPostcodesSelector,
  lookupTableSelector,
  groupedPostcodesSelector,
  (partialPostcodes, lookUpTables, groupedPostcodes) => {
    if (!partialPostcodes || !lookUpTables || !groupedPostcodes)
      return { OUTER: [], SECTOR: [], POSTCODE: [] };

    const outer = [];
    const sector = [];
    const postcodes = [];

    partialPostcodes.forEach((sectorLabel) => {
      const outerKey = sectorLabel.substr(0, sectorLabel.length - 2);
      if (lookUpTables.OUTER[outerKey]) {
        outer.push(lookUpTables.OUTER[outerKey]);
      }

      if (lookUpTables.SECTOR[sectorLabel]) {
        sector.push(lookUpTables.SECTOR[sectorLabel]);
      }
      if (groupedPostcodes[sectorLabel]) {
        postcodes.push(
          ...groupedPostcodes[sectorLabel].map(
            (key) => lookUpTables.POSTCODE[key]
          )
        );
      } else if (groupedPostcodes[sectorLabel.split(" ").join("")]) {
        postcodes.push(
          ...groupedPostcodes[sectorLabel.split(" ").join("")].map(
            (key) => lookUpTables.POSTCODE[key]
          )
        );
      } else if (
        sectorLabel[2] === " " &&
        groupedPostcodes[sectorLabel.split(" ").join("  ")]
      ) {
        postcodes.push(
          ...groupedPostcodes[sectorLabel.split(" ").join("  ")].map(
            (key) => lookUpTables.POSTCODE[key]
          )
        );
      }
    });

    return {
      OUTER: [...new Set(outer)],
      SECTOR: sector,
      POSTCODE: postcodes.flat(),
    };
  }
);

export const lookUpTableOuterSelector = createSelector(
  lookupTableSelector,
  (lookupTable) => {
    if (!lookupTable?.OUTER) return [];

    return Object.keys(lookupTable.OUTER).map((outer) => ({ outer }));
  }
);
